import React from "react";
import { trackEvent } from "utils/analytics";
import { SurfaceType, ActionType } from "utils/analytics/analyticsConstants";
import { envWithDefault } from "utils/env";
import { HelpLinks } from "utils/helpLinks";
import NoPII from "utils/NoPII";
const ZendeskKey = envWithDefault("ZENDESK_LIVE_CHAT_API_KEY");

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    zE: any;
  }
}

type SupportedErrorScenarios =
  | "pending_account"
  | "pending_account_live_support";

/*
  This is a hack we are introducing to remove the "Talk to a human" button
  that gets automatically added by Zendesk at the end of every non happy workflow.
  From Zendesk Dashboard, we do not have control over hiding/renaming/changing this button.

  Here, we look inside chat window for every element that gets added and in case its a "Talk to a human"
  button then we replace it.
  We made it a bit more generic hoping in case Zendesk changes the text/spacing slightly, we are still covered.
*/
function replaceTalkToHumanButton(errorMessage: SupportedErrorScenarios) {
  const iframe = document.querySelector(
    'iframe[title="Messaging window"]',
  ) as HTMLIFrameElement;
  if (!iframe || !iframe.contentWindow) {
    // @assert - We assume the Zendesk widget to completely load in < 1 second,
    // so ideally this should not execute indefinitely
    const TIMEOUT_DELAY_TO_WAIT_FOR_ZENDESK_LIVE_CHAT_TO_ACTIVATE = 300;
    setTimeout(
      () => replaceTalkToHumanButton(errorMessage),
      TIMEOUT_DELAY_TO_WAIT_FOR_ZENDESK_LIVE_CHAT_TO_ACTIVATE,
    );
    return;
  }

  iframe.contentWindow.document.addEventListener(
    "DOMNodeInserted",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      const button = e.relatedNode.querySelector(
        "button[data-garden-id='buttons.button']:last-child",
      );
      if (!button) {
        // No button was added.
        return;
      }

      if (button.innerText.indexOf("human") >= 0) {
        const clone = button.cloneNode(true);
        clone.innerText = "Submit a Request";
        button.replaceWith(clone);
        clone.addEventListener("click", () => {
          trackEvent({
            eventName: "clicked_contact_us",
            eventType: ActionType.CLICK_BUTTON,
            analyticsSurface: SurfaceType.ZENDESK_LIVE_CHAT,
            extraProperties: {
              extraMetadata: {
                errorType: NoPII(errorMessage),
              },
            },
          });
          window.open(HelpLinks.NEW_REQUEST);
        });
      }
    },
  );
}

const loadLiveChatWidget = (errorMessage: SupportedErrorScenarios) => {
  if (isZendeskLoaded()) {
    // Script has already been loaded
    return;
  }

  const existingScript = document.getElementById("ze-snippet");
  if (existingScript) {
    // Script has been added to DOM but hasn't loaded yet.
    return;
  }

  const script = document.createElement("script");
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZendeskKey}`;
  script.id = "ze-snippet";
  document.body.appendChild(script);
  script.onload = () => replaceTalkToHumanButton(errorMessage);
};

const isZendeskLoaded = () => {
  return !!window.zE;
};

const ZendeskAPI = (...args: Array<string | boolean>) => {
  if (isZendeskLoaded()) {
    window.zE.apply(null, args);
  }
};

export function openZendeskMessenger() {
  ZendeskAPI("messenger", "open");
}

export const LiveChat = ({
  errorMessage,
}: {
  errorMessage: SupportedErrorScenarios;
}) => {
  React.useEffect(() => {
    loadLiveChatWidget(errorMessage);
    return function unloadLiveChatWidget() {
      ZendeskAPI("messenger:set", "cookies", false);
      delete window.zE;
    };
  }, [errorMessage]);

  return null;
};
